import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { ArrowRightLeft, Search, FileText, Replace } from 'lucide-react';

export default function FindReplaceModal({ isOpen, onClose, document, onReplace }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWords, setWholeWords] = useState(false);
  const [matchCount, setMatchCount] = useState(0);

  // Count matches when search term changes
  useEffect(() => {
    if (!searchTerm || !document?.content) {
      setMatchCount(0);
      return;
    }

    try {
      const content = document.content;
      let flags = 'g';
      if (!matchCase) flags += 'i';
      
      let pattern = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (wholeWords) {
        pattern = `\\b${pattern}\\b`;
      }
      
      const regex = new RegExp(pattern, flags);
      const matches = content.match(regex);
      setMatchCount(matches ? matches.length : 0);
    } catch {
      setMatchCount(0);
    }
  }, [searchTerm, matchCase, wholeWords, document?.content]);

  const handleReplaceAll = useCallback(() => {
    if (!searchTerm || !document?.content || !onReplace) return;

    try {
      let flags = 'g';
      if (!matchCase) flags += 'i';
      
      let pattern = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (wholeWords) {
        pattern = `\\b${pattern}\\b`;
      }
      
      const regex = new RegExp(pattern, flags);
      const newContent = document.content.replace(regex, replaceTerm);
      
      onReplace(newContent, matchCount);
      setSearchTerm('');
      setReplaceTerm('');
      onClose();
    } catch (error) {
      console.error('Replace failed:', error);
    }
  }, [searchTerm, replaceTerm, matchCase, wholeWords, document?.content, onReplace, matchCount, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="find-replace-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <ArrowRightLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <span>Find & Replace</span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Document Info */}
          <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="font-medium text-slate-900 dark:text-white">
              {document?.title || 'Untitled'}
            </span>
          </div>

          {/* Search Input */}
          <div className="space-y-2">
            <Label>Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Find text..."
                className="pl-10 pr-20"
                data-testid="find-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                {matchCount} matches
              </span>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="matchCase"
                checked={matchCase}
                onCheckedChange={setMatchCase}
                data-testid="match-case-checkbox"
              />
              <Label htmlFor="matchCase" className="text-sm cursor-pointer">
                Match case
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="wholeWords"
                checked={wholeWords}
                onCheckedChange={setWholeWords}
                data-testid="whole-words-checkbox"
              />
              <Label htmlFor="wholeWords" className="text-sm cursor-pointer">
                Whole words
              </Label>
            </div>
          </div>

          {/* Replace Input */}
          <div className="space-y-2">
            <Label>Replace</Label>
            <div className="relative">
              <Replace className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={replaceTerm}
                onChange={(e) => setReplaceTerm(e.target.value)}
                placeholder="Replace with..."
                className="pl-10"
                data-testid="replace-input"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleReplaceAll}
              disabled={!searchTerm || matchCount === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="replace-all-button"
            >
              Replace All ({matchCount})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
